// apps/workflow/src/pages/layouts/AdminLayout.jsx
import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  ClipboardList,
  FileCheck,
  DollarSign,
  CreditCard,
  User,
  Users,
  LogOut,
  Menu,
  X,
  ExternalLink,
  Shield,
  ChevronDown,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const LOGO_URL = 'https://housedesigns99.com/api/files/f5eolh49d789mqi/rwth7jtsmlcst1n/qudexa_logo_fevicon_cmzEb5UuHA.png';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardDropdownOpen, setDashboardDropdownOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = user?.role || 'admin';
  const isAdmin = ['admin', 'super_admin', 'manager'].includes(userRole);
  const isFreelancer = userRole === 'freelancer';
  const isDesigner = userRole === 'designer';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    const fullPath = location.pathname;
    if (path === 'dashboard') return fullPath === '/designer' || fullPath === '/designer/dashboard';
    return fullPath.startsWith(`/designer/${path}`);
  };

  // ─── Menu items with conditional visibility ──────────────
  const menuItems = [
    { path: 'dashboard', label: 'Designer Dashboard', icon: LayoutDashboard, color: 'text-blue-400', show: true },
    { path: 'tasks', label: 'All Tasks', icon: ClipboardList, color: 'text-cyan-400', show: true },
    { path: 'submissions', label: 'My Submissions', icon: FileCheck, color: 'text-purple-400', show: true },
    // Earnings – visible to freelancers and admins, hidden for designers
    { path: 'earnings', label: 'Earnings', icon: DollarSign, color: 'text-green-400', show: !isDesigner },
    // Payments – only for freelancers (their own earnings)
    { path: 'payments', label: 'My Payments', icon: CreditCard, color: 'text-amber-400', show: isFreelancer },
    // 👇 NEW: Admin payment management – only for admin roles
    { path: 'admin/payments', label: 'Payments Mgmt', icon: CreditCard, color: 'text-emerald-400', show: isAdmin },
    { path: 'profile', label: 'Profile', icon: User, color: 'text-amber-400', show: true },
    // Admin-only items
    { path: 'admin/users', label: 'Users', icon: Users, color: 'text-rose-400', show: isAdmin },
    { path: 'admin/freelancers', label: 'Freelancers', icon: Users, color: 'text-pink-400', show: isAdmin },
  ];

  const visibleMenuItems = menuItems.filter(item => item.show);

  const dashboardOptions = [
    { label: '👤 Client Dashboard', path: '/client/dashboard' },
    { label: '🎨 Designer Dashboard', path: '/designer/dashboard' },
    { label: '💼 Freelancer Dashboard', path: '/designer/dashboard' },
    { label: '⚙️ Admin Panel', path: '/designer/admin/users' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top Bar */}
      <header className="bg-white shadow-sm px-6 py-3 flex items-center justify-between border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
          <span className="text-xl font-bold text-slate-800">
            Admin <span className="text-amber-500">Panel</span>
          </span>
        </div>

        {/* Dashboard Switcher Dropdown */}
        <div className="relative">
          <button
            onClick={() => setDashboardDropdownOpen(!dashboardDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition"
          >
            <LayoutDashboard className="w-4 h-4" />
            Switch Dashboard
            <ChevronDown className={`w-4 h-4 transition-transform ${dashboardDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {dashboardDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
              {dashboardOptions.map((opt) => (
                <Link
                  key={opt.path}
                  to={opt.path}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition"
                  onClick={() => setDashboardDropdownOpen(false)}
                >
                  {opt.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">{user?.name || 'Admin'}</span>
          <span className="text-xs bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full font-medium">
            {userRole}
          </span>
          <button
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500 transition p-2 hover:bg-red-50 rounded-lg"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <button
          className="lg:hidden fixed bottom-4 left-4 z-50 p-2 bg-white rounded-md shadow"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>

        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 via-gray-900 to-black text-white shadow-2xl transform transition-transform duration-300 lg:translate-x-0 flex flex-col",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex justify-between items-center p-5 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
              <h1 className="text-xl font-bold tracking-tight">Admin Hub</h1>
            </div>
            <button className="lg:hidden p-1 text-white/70 hover:text-white" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-white/10 shrink-0">
            <p className="text-xs font-medium text-indigo-300 uppercase tracking-wider">Logged in as</p>
            <p className="font-semibold text-white">{user?.name || 'Admin'} <span className="text-indigo-300 text-sm">({userRole})</span></p>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-4">
            <div className="space-y-1">
              {visibleMenuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                    isActive(item.path)
                      ? "bg-amber-500/20 text-amber-300 shadow-lg shadow-amber-500/10 border-r-4 border-amber-400"
                      : "text-indigo-200 hover:bg-white/10 hover:text-white"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`w-5 h-5 ${item.color} flex-shrink-0`} />
                  <span className="whitespace-nowrap">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10 p-3 shrink-0 space-y-2">
            <a
              href="http://localhost:3000"
              className="flex items-center gap-3 px-3 py-2 text-indigo-200 hover:bg-white/10 hover:text-white rounded-lg transition"
            >
              <ExternalLink className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Homepage</span>
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 w-full text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-lg transition"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Logout</span>
            </button>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto p-6 lg:ml-72">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;